// src/components/PrediccionesTabla.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";

function formatDate(ts) {
  // ts puede ser Timestamp de Firestore o null (cuando serverTimestamp aún no llega)
  if (!ts || !ts.seconds) return "—";
  const d = new Date(ts.seconds * 1000);
  return d.toLocaleString(); // puedes ajustar locale si quieres
}

function blockLabel(blockType) {
  if (blockType === "name") return "Nombre/Familia";
  if (blockType === "cedula") return "Cédula";
  if (blockType === "dob") return "Nacimiento";
  return blockType || "—";
}

function renderInputs(row) {
  const inputs = row?.inputs || {};

  // Por bloque
  if (row.blockType === "name") {
    const a = inputs.fullName || "—";
    const b = inputs.fatherName || "—";
    const c = inputs.motherName || "—";
    return `Nombre: ${a} | Padre: ${b} | Madre: ${c}`;
  }

  if (row.blockType === "cedula") {
    return `Cédula: ${inputs.cedula || "—"}`;
  }

  if (row.blockType === "dob") {
    const dob = inputs.dob || "—";
    const bd = row?.extra?.dobBreakdown;
    if (bd) {
      return `Fecha: ${dob} | Día:${bd.day} Mes:${bd.month} Año:${bd.year} LP:${bd.lifePath}`;
    }
    return `Fecha: ${dob}`;
  }

  // Fallback general
  const pairs = Object.entries(inputs)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}: ${v}`);
  return pairs.length ? pairs.join(" | ") : "—";
}

export default function PrediccionesTabla() {
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState("asc"); // "asc" = más antiguo primero
  const [search, setSearch] = useState("");

  // paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const q = query(collection(db, "predicciones"), orderBy("createdAt", sort));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setRows(data);
    });

    return () => unsub();
  }, [sort]);

  const filtered = useMemo(() => {
    const s = (search || "").trim().toLowerCase();
    if (!s) return rows;

    return rows.filter((r) => {
      const u = r.user || {};
      const inputs = r.inputs || {};

      const haystack = [
        u.username,
        u.phone,
        r.blockType,
        inputs.fullName,
        inputs.fatherName,
        inputs.motherName,
        inputs.cedula,
        inputs.dob,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(s);
    });
  }, [rows, search]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filtered.slice(start, end);
  }, [filtered, page, rowsPerPage]);

  // stats rápidos
  const total = filtered.length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 12% 18%, rgba(25,118,210,0.14) 0%, transparent 46%), radial-gradient(circle at 88% 82%, rgba(76,175,80,0.12) 0%, transparent 42%), #0b1220",
        py: 5,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 900, color: "white" }}>
            📋 Predicciones - Tabla de Uso
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.75)", mt: 1 }}>
            Listado de intentos (cada click “Generar” crea un registro nuevo).
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mt: 2 }}
          >
            <TextField
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Buscar por usuario, teléfono, cédula, nombre..."
              fullWidth
              sx={{
                maxWidth: 520,
                background: "rgba(255,255,255,0.92)",
                borderRadius: 2,
              }}
            />

            <ToggleButtonGroup
              value={sort}
              exclusive
              onChange={(_, v) => v && setSort(v)}
              sx={{
                background: "rgba(255,255,255,0.92)",
                borderRadius: 2,
              }}
            >
              <ToggleButton value="asc">Más antiguo</ToggleButton>
              <ToggleButton value="desc">Más reciente</ToggleButton>
            </ToggleButtonGroup>

            <Chip
              label={`Registros: ${total}`}
              sx={{
                color: "white",
                backgroundColor: "rgba(25,118,210,0.35)",
                border: "1px solid rgba(255,255,255,0.18)",
                fontWeight: 800,
              }}
            />
          </Stack>
        </Box>

        <Card
          sx={{
            borderRadius: 3,
            background: "rgba(255,255,255,0.92)",
            boxShadow: "0 12px 35px rgba(0,0,0,0.28)",
            overflow: "hidden",
          }}
        >
          <CardContent>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Teléfono</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Invitado</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Datos llenados</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paged.map((r) => {
                    const u = r.user || {};
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>{formatDate(r.createdAt)}</TableCell>

                        <TableCell>
                          {u.username ? (
                            <Chip
                              size="small"
                              label={`@${u.username}`}
                              sx={{
                                backgroundColor: "rgba(25,118,210,0.12)",
                                fontWeight: 800,
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        <TableCell>{u.phone || "—"}</TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={blockLabel(r.blockType)}
                            sx={{
                              fontWeight: 800,
                              backgroundColor:
                                r.blockType === "name"
                                  ? "rgba(156,39,176,0.12)"
                                  : r.blockType === "cedula"
                                  ? "rgba(76,175,80,0.12)"
                                  : "rgba(244,67,54,0.12)",
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          {u.isGuest ? (
                            <Chip
                              size="small"
                              label="Sí"
                              sx={{ backgroundColor: "rgba(255,152,0,0.18)", fontWeight: 800 }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="No"
                              sx={{ backgroundColor: "rgba(76,175,80,0.15)", fontWeight: 800 }}
                            />
                          )}
                        </TableCell>

                        <TableCell sx={{ maxWidth: 520, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {renderInputs(r)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {paged.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No hay registros para mostrar.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
